import { useId, useState } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/utils'

/**
 * Constituent PasswordInput
 *
 * Plain text/password input with an inline "show / hide" toggle. Same sizing
 * rhythm as the regular text inputs used across the auth and account screens
 * so the toggle aligns without per-form adjustments. The toggle button is a
 * real <button> with aria-pressed so screen readers announce the active state.
 */
export interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode
  /** Optional hint text rendered under the field. */
  hint?: ReactNode
  /** Show a visible error message with role=alert. */
  error?: ReactNode
}

function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="h-5 w-5">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    )
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="h-5 w-5">
      <path d="M17.94 17.94A10.94 10.94 0 0112 20c-7 0-11-8-11-8a19.6 19.6 0 014.22-5.59" />
      <path d="M9.9 4.24A10.94 10.94 0 0112 4c7 0 11 8 11 8a19.7 19.7 0 01-3.16 4.19" />
      <path d="M14.12 14.12A3 3 0 119.88 9.88" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  )
}

export default function PasswordInput({
  label,
  hint,
  error,
  className,
  id: providedId,
  'aria-describedby': describedBy,
  ...rest
}: PasswordInputProps) {
  const generatedId = useId()
  const id = providedId ?? generatedId
  const [visible, setVisible] = useState(false)
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const internalDescriptionId = error ? errorId : hint ? hintId : undefined
  const descriptionIds = [describedBy, internalDescriptionId].filter(Boolean).join(' ') || undefined

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={id}
          className="mb-1 block text-sm font-medium text-slate-700"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <input
          {...rest}
          id={id}
          type={visible ? 'text' : 'password'}
          aria-describedby={descriptionIds}
          aria-invalid={error ? true : rest['aria-invalid']}
          className={cn(
            'block w-full rounded-lg border border-slate-300 px-3 py-2 pr-12 text-sm text-slate-900 placeholder:text-slate-400',
            'focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500',
            'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-500',
            className,
          )}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          aria-pressed={visible}
          aria-label={visible ? 'Hide password' : 'Show password'}
          className={cn(
            'absolute inset-y-0 right-0 flex items-center justify-center px-3 text-slate-500',
            'hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-r-lg',
          )}
        >
          <EyeIcon open={visible} />
        </button>
      </div>
      {hint && !error && (
        <p id={hintId} className="mt-1 text-xs text-slate-500">{hint}</p>
      )}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-xs text-rose-700">{error}</p>
      )}
    </div>
  )
}
